/**
 * The subscribe popups. Each is a <dialog popover> in the page that its
 * Subscribe buttons open without any script. This upgrades them: the popup
 * opens as a modal dialog (the page behind goes inert and the browser hands
 * focus back to the button on close), Copy copies the link, and the three
 * guides become tabs. The motion is in the stylesheet; closing waits for
 * its exit to finish, since not every browser can animate a dialog leaving
 * the top layer on its own.
 */

/** How long "Copied" stays before the button resets. */
const COPIED_MS = 6000;

/**
 * Copies text, falling back to selecting it and using execCommand where the
 * Clipboard API is missing or refused (older Safari, non-secure origins).
 */
async function copyText(text: string, fallbackElement: HTMLElement): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    selectContents(fallbackElement);
    // execCommand is deprecated, but it is the only fallback some browsers have.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return document.execCommand("copy");
  }
}

function selectContents(element: HTMLElement): void {
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(element);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/** Longest wait for the exit before closing anyway, in case it never runs. */
const CLOSE_FALLBACK_MS = 400;

/**
 * Plays the popup's exit (the stylesheet's [data-closing] state), then closes
 * it. Reopening part-way through cancels the close.
 */
function closeAnimated(dialog: HTMLDialogElement): void {
  if (!dialog.open || dialog.dataset["closing"] !== undefined) return;
  dialog.dataset["closing"] = "";
  const exits = dialog.getAnimations().map((animation) => animation.finished);
  const timeout = new Promise((resolve) => window.setTimeout(resolve, CLOSE_FALLBACK_MS));
  void Promise.race([Promise.allSettled(exits), timeout]).then(() => {
    if (dialog.dataset["closing"] === undefined) return;
    delete dialog.dataset["closing"];
    dialog.close();
  });
}

/** Turns the guides' open / close sections into a row of tabs, Google first. */
function makeTabs(dialog: HTMLDialogElement): void {
  const guides = [...dialog.querySelectorAll<HTMLDetailsElement>("details.guide")];
  const title = dialog.querySelector(".guides-title");
  if (guides.length === 0 || !title) return;

  const tablist = document.createElement("div");
  tablist.className = "guide-tabs";
  tablist.setAttribute("role", "tablist");
  tablist.setAttribute("aria-labelledby", title.id);

  const tabs = guides.map((details) => {
    const summary = details.querySelector("summary");
    const panel = details.querySelector<HTMLElement>(".guide-steps");
    const tab = document.createElement("button");
    tab.type = "button";
    tab.id = `${panel?.id ?? ""}-tab`;
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-controls", panel?.id ?? "");
    tab.append(...[...(summary?.childNodes ?? [])].map((node) => node.cloneNode(true)));
    // Several open sections sharing a name close each other; tabs show one at a time instead.
    details.removeAttribute("name");
    details.open = true;
    if (summary) summary.hidden = true;
    panel?.setAttribute("role", "tabpanel");
    panel?.setAttribute("aria-labelledby", tab.id);
    tablist.append(tab);
    return tab;
  });

  const select = (index: number, focus: boolean): void => {
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const details = guides[i];
      if (details) details.hidden = !selected;
    });
    if (focus) tabs[index]?.focus();
  };

  tabs.forEach((tab, i) => {
    tab.addEventListener("click", () => {
      select(i, false);
    });
  });
  // Arrow keys move between tabs, Home and End jump to the ends.
  tablist.addEventListener("keydown", (event) => {
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current < 0) return;
    const next = {
      ArrowLeft: (current - 1 + tabs.length) % tabs.length,
      ArrowRight: (current + 1) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    select(next, true);
  });

  title.after(tablist);
  select(0, false);
}

/** Wires Copy, the guide tabs, Close, the backdrop and the Tab key once per popup. */
function enhance(dialog: HTMLDialogElement): void {
  if (dialog.dataset["enhanced"] !== undefined) return;
  dialog.dataset["enhanced"] = "";

  const link = dialog.querySelector<HTMLElement>(".link-field");
  const copy = dialog.querySelector<HTMLButtonElement>(".copy");
  const status = dialog.querySelector<HTMLElement>(".copy-status");
  let copiedTimer: number | undefined;

  const resetCopy = (): void => {
    window.clearTimeout(copiedTimer);
    if (copy) delete copy.dataset["copied"];
    if (status) {
      status.textContent = "";
      status.className = "copy-status";
    }
  };
  dialog.addEventListener("close", resetCopy);

  if (link && copy && status) {
    copy.hidden = false;
    copy.addEventListener("click", () => {
      void copyText(link.textContent, link).then((copied) => {
        resetCopy();
        if (copied) {
          copy.dataset["copied"] = "";
          status.textContent = "Link copied";
          status.classList.add("visually-hidden");
          copiedTimer = window.setTimeout(resetCopy, COPIED_MS);
        } else {
          selectContents(link);
          status.textContent = "Couldn't copy. Select the link and copy it instead.";
          status.classList.add("copy-error");
        }
      });
    });
    // One tap or focus selects the whole link, ready to copy by hand.
    link.addEventListener("focus", () => {
      selectContents(link);
    });
  }

  makeTabs(dialog);

  dialog.querySelector(".subscribe-close")?.addEventListener("click", (event) => {
    if (!dialog.open) return;
    event.preventDefault();
    closeAnimated(dialog);
  });

  // Escape: play the exit too, where the browser lets the close wait.
  dialog.addEventListener("cancel", (event) => {
    if (!event.cancelable) return;
    event.preventDefault();
    closeAnimated(dialog);
  });

  // Clicks on the backdrop land on the dialog element itself, outside its box.
  dialog.addEventListener("click", (event) => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (!inside) closeAnimated(dialog);
  });

  // A modal dialog makes the page behind it inert, but Tab can still leave
  // for the browser's own controls; keep it cycling inside the dialog.
  dialog.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;
    const focusable = [
      ...dialog.querySelectorAll<HTMLElement>(
        "button:not([tabindex='-1']), a[href], [tabindex]:not([tabindex='-1'])",
      ),
    ].filter((element) => element.offsetParent !== null || element === document.activeElement);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/** Opens every subscribe popup as a modal dialog instead of a popover. */
export function setUpSubscribePopups(): void {
  document.addEventListener("click", (event) => {
    const opener = (event.target as Element | null)?.closest("button[popovertarget]");
    if (!opener || opener.getAttribute("popovertargetaction") === "hide") return;
    const dialog = document.getElementById(opener.getAttribute("popovertarget") ?? "");
    if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains("subscribe")) return;
    // Stops the button opening it as a popover too.
    event.preventDefault();
    enhance(dialog);
    // Opened as a popover before this script arrived: swap it for the modal.
    if (dialog.matches(":popover-open")) dialog.hidePopover();
    // Reopened while closing: stay open.
    delete dialog.dataset["closing"];
    if (!dialog.open) dialog.showModal();
  });
}
